import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import './CreditNotification.css';

const CreditNotification = ({ credits, isVisible, onComplete, type = 'daily' }) => {
    const { t } = useTranslation();
    const [particles, setParticles] = useState([]);

    useEffect(() => {
        if (isVisible) {
            // Generate floating particles
            const newParticles = Array.from({ length: 12 }, (_, i) => ({
                id: i,
                x: Math.random() * 100 - 50,
                y: Math.random() * -100 - 50,
                rotation: Math.random() * 360,
                scale: 0.5 + Math.random() * 0.5,
                delay: Math.random() * 0.3,
            }));
            setParticles(newParticles);

            // Auto-hide after animation
            const timer = setTimeout(() => {
                onComplete?.();
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, [isVisible, onComplete]);

    const getIcon = () => {
        switch (type) {
            case 'purchase':
                return '💎';
            case 'bonus':
                return '🎁';
            case 'daily':
            default:
                return '✨';
        }
    };

    const getMessage = () => {
        switch (type) {
            case 'purchase':
                return t('creditNotification.purchased');
            case 'bonus':
                return t('creditNotification.bonus');
            case 'daily':
            default:
                return t('creditNotification.daily');
        }
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className="credit-notification-container"
                    initial={{ opacity: 0, y: -100, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -50, scale: 0.8 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                >
                    {/* Floating particles */}
                    {particles.map((particle) => (
                        <motion.div
                            key={particle.id}
                            className="credit-particle"
                            initial={{ 
                                opacity: 0, 
                                x: 0, 
                                y: 0, 
                                scale: 0,
                                rotate: 0 
                            }}
                            animate={{ 
                                opacity: [0, 1, 1, 0], 
                                x: particle.x, 
                                y: particle.y,
                                scale: particle.scale,
                                rotate: particle.rotation
                            }}
                            transition={{ 
                                duration: 2,
                                delay: particle.delay,
                                ease: 'easeOut'
                            }}
                        >
                            ⭐
                        </motion.div>
                    ))}

                    {/* Main notification */}
                    <motion.div 
                        className="credit-notification"
                        animate={{
                            boxShadow: [
                                '0 0 20px rgba(255, 215, 0, 0.3)',
                                '0 0 40px rgba(255, 215, 0, 0.6)',
                                '0 0 20px rgba(255, 215, 0, 0.3)',
                            ],
                        }}
                        transition={{ repeat: 2, duration: 0.8 }}
                    >
                        <div className="notification-icon">{getIcon()}</div>
                        
                        <div className="notification-content">
                            <span className="notification-message">{getMessage()}</span>
                            <motion.div 
                                className="notification-credits"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.3, type: 'spring', stiffness: 400 }}
                            >
                                <span className="credits-plus">+</span>
                                <motion.span
                                    className="credits-value"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.5 }}
                                >
                                    {credits?.toLocaleString()}
                                </motion.span>
                                <Icon name="star" className="credits-icon" />
                            </motion.div>
                        </div>

                        {/* Progress bar that shrinks */}
                        <motion.div
                            className="notification-progress"
                            initial={{ scaleX: 1 }}
                            animate={{ scaleX: 0 }}
                            transition={{ duration: 4, ease: 'linear' }}
                        />
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CreditNotification;
